"use client";

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import type { PaginationMeta } from "@/components/frontend/ui/pagination";
import type { Partner } from "@/lib/types/marketsuite/datatypes/partner";
import type { PartnerOverviewBlock } from "@/payload-types";
import { sortOptionValues } from "./constants";
import type { SelectedFilters } from "@/lib/blocks/render-filters";
import { FILTER_TYPE_DEFINITIONS } from "@/lib/marketsuite/filters/registry";
import { buildFilterQuery } from "@/lib/marketsuite/filters";
import { buildFilterDefaultValues } from "@/lib/marketsuite/filters/default-values";
import { getRelationshipObject } from "@/lib/utils/theme-configuration-class";

export type PartnerOverviewResultsData = {
	data: Partner[];
	meta: PaginationMeta;
};

type PartnerOverviewContextValue = {
	basePath: string;
	detailUrlBase: string | undefined;
	data?: PartnerOverviewResultsData;
	settings: PartnerOverviewBlock["settings"];
	filters: PartnerOverviewBlock["filters"];
	selectedFilters: SelectedFilters;
	defaultSelectedFilters: SelectedFilters;
	hasAppliedFilters: boolean;
	pending: boolean;
	sort: string;
	onPageChange: (page: number) => void;
	onSortChange: (sort: string) => void;
	onFiltersChange: (filters: SelectedFilters) => void;
};

type PartnerOverviewProviderProps = {
	basePath: string;
	children: ReactNode;
	detailUrlBase: string | undefined;
	initialResult: PartnerOverviewResultsData;
	settings: PartnerOverviewBlock["settings"];
	filters: PartnerOverviewBlock["filters"];
	defaultFilters: Record<string, string> | undefined;
};

const PartnerOverviewContext = createContext<PartnerOverviewContextValue | undefined>(undefined);
const partnerFilters = FILTER_TYPE_DEFINITIONS.filter((definition) => definition.scopes.includes("partner"));

function getPage(value: string | null): number {
	const page = parseInt(value ?? "1", 10);
	return Number.isFinite(page) && page > 0 ? page : 1;
}

export function PartnerOverviewState({
	basePath,
	children,
	detailUrlBase,
	initialResult,
	settings,
	filters,
	defaultFilters,
	query,
}: PartnerOverviewProviderProps & { query: string }) {
	const locale = useLocale();
	const router = useRouter();
	const searchParams = new URLSearchParams(query);
	const page = getPage(searchParams.get("page"));
	const sortParam = searchParams.get("sort");
	const defaultSort = settings?.defaultSort ?? "";
	const sort = sortParam === "" || (sortParam && sortOptionValues.includes(sortParam)) ? sortParam : defaultSort;
	const hiddenFilters = filters?.defaultFilters?.hidden ?? [];
	const defaultSelectedFilters = buildFilterDefaultValues(getRelationshipObject(filters?.filterset), defaultFilters) as SelectedFilters;
	const selectedFilters: SelectedFilters = { ...defaultSelectedFilters };

	for (const definition of partnerFilters) {
		// Hidden filters are locked to their default value, so URL overrides are ignored.
		if (hiddenFilters.includes(definition.slug)) continue;

		const values = searchParams.getAll(definition.slug).filter(Boolean);
		if (values.length === 0) continue;

		if (definition.kind === "range") {
			const range = values
				.flatMap((value) => value.split(","))
				.map((value) => parseFloat(value))
				.filter((value) => !Number.isNaN(value));

			if (range.length > 0) selectedFilters[definition.slug] = range;
			continue;
		}

		if (definition.kind === "select") {
			const defaults = defaultSelectedFilters[definition.slug];
			selectedFilters[definition.slug] = [...new Set([...(Array.isArray(defaults) ? defaults.map((value) => value.toString()) : []), ...values])];
			continue;
		}

		selectedFilters[definition.slug] = values;
	}

	const hasAppliedFilters = buildFilterQuery({ selectedFilters, defaultFilters, hiddenFilters, listingType: undefined }).length > 0;

	const { data, isFetching } = useQuery({
		queryKey: ["partners", page, sort, selectedFilters, settings?.randomSort ?? false],
		queryFn: async () => {
			const params = new URLSearchParams({ page: page.toString() });
			if (sort) params.set("sort", sort);
			if (settings?.randomSort) params.set("random-sort", "true");

			for (const [key, values] of Object.entries(selectedFilters)) {
				if (values == null) continue;

				if (Array.isArray(values)) {
					for (const value of values) params.append(key, value.toString());
				} else {
					params.set(key, values.toString());
				}
			}

			const response = await fetch(`/${locale}/next/partner-overview?${params.toString()}`);

			if (!response.ok) throw new Error("Failed to fetch partners.");

			return response.json() as Promise<PartnerOverviewResultsData>;
		},
		initialData: page === 1 && sort === defaultSort && !hasAppliedFilters ? initialResult : undefined,
		placeholderData: (previousData) => previousData ?? initialResult,
	});

	const updateParams = (nextPage: number, nextSort: string, nextFilters: SelectedFilters = selectedFilters) => {
		const params = new URLSearchParams(searchParams);

		for (const key of ["page", "sort", ...partnerFilters.map((definition) => definition.slug)]) params.delete(key);

		const filterQuery = buildFilterQuery({
			selectedFilters: nextFilters,
			defaultFilters,
			hiddenFilters,
			listingType: undefined,
			page: nextPage,
			sort: nextSort === defaultSort ? undefined : nextSort,
		});

		for (const [key, value] of new URLSearchParams(filterQuery)) params.append(key, value);

		const query = params.toString();
		router.push(query ? `${basePath}?${query}` : basePath, { scroll: false });
	};

	return (
		<PartnerOverviewContext.Provider
			value={{
				basePath,
				detailUrlBase,
				data,
				settings,
				filters,
				selectedFilters,
				defaultSelectedFilters,
				hasAppliedFilters,
				pending: isFetching,
				sort,
				onPageChange: (nextPage) => updateParams(nextPage, sort),
				onSortChange: (nextSort) => updateParams(1, nextSort),
				onFiltersChange: (nextFilters) => updateParams(1, sort, nextFilters),
			}}
		>
			{children}
		</PartnerOverviewContext.Provider>
	);
}

export function PartnerOverviewProvider(props: PartnerOverviewProviderProps) {
	return <PartnerOverviewState {...props} query={useSearchParams().toString()} />;
}

export function usePartnerOverview() {
	const context = useContext(PartnerOverviewContext);

	if (context === undefined) {
		throw new Error("usePartnerOverview must be used within a PartnerOverviewProvider");
	}

	return context;
}
